// GPU renderer for the graph's halftone dither fields.
//
// The old path rebuilt the whole raster on the CPU every redraw: evaluate the
// density field at every tile, accumulate tens of thousands of Path2D arcs,
// and let canvas 2D analytically rasterize each antialiased circle (~50ms per
// field per redraw — the main HUD-animation jank source). Here the CPU only
// evaluates the field at TILE resolution (a ~200×100 Float32Array of final
// dot radii, ~2ms, reusing the exact same density math), and a fragment
// shader turns that into dots: each pixel maps to its tile neighborhood,
// reads the dot radius texel, and does a distance test with 1px smoothstep
// antialiasing. Per-frame CPU cost is a couple of small texture uploads.
//
// See computeDitherFieldGLLayer (RedAboveLossOfControlOverlay.tsx) for how a
// canvas layer's settings/shade field get baked into the radius grid, and
// GraphCanvasLayer (ExtrapolatedGraphRenderer.tsx) for the integration + the
// canvas-2D fallback when WebGL2 is unavailable or settings need the old path.

export type DitherFieldGLLayer = {
  // Per-tile FINAL dot radius in css px (0 = no dot), cols × rows, row-major.
  radii: Float32Array
  cols: number
  rows: number
  // Screen-space grid: tile-left x of column 0, column pitch, tile-top y of
  // row 0, and the (row) tile size. Pitch == tileSize unless edge-flush.
  gridFirstTileLeftX: number
  pitch: number
  gridStartY: number
  tileSize: number
  // Horizontal extent driving the flush-left→flush-right dot alignment lerp
  // (the plot span for edge-flush layouts, else the raster bounds).
  rasterOriginX: number
  fieldWidth: number
  // Set (to the max dot radius) when the edge-flush column layout is active:
  // dots then slide within the column's max-dot extent (center ± (cap -
  // radius)) instead of within the tile, so the first/last columns stay
  // flush at the plot edges without ever crossing them. Null → legacy
  // within-tile slide.
  edgeFlushMaxDotRadius: number | null
  // Optional per-column vertical clip: cols × 2 (upperY, lowerY) in screen px;
  // pixels outside [upperY, lowerY] fade out over 1px. Mirrors the old path's
  // clip to the band's fill path. Null → no clip.
  clipBounds: Float32Array | null
  color: [number, number, number, number]
  opacity: number
  dotShape: 'circle' | 'square'
}

const VERTEX_SHADER = `#version 300 es
void main() {
  // Fullscreen triangle from gl_VertexID; no buffers needed.
  vec2 position = vec2(
    gl_VertexID == 1 ? 3.0 : -1.0,
    gl_VertexID == 2 ? 3.0 : -1.0
  );
  gl_Position = vec4(position, 0.0, 1.0);
}
`

const FRAGMENT_SHADER = `#version 300 es
precision highp float;

uniform sampler2D uRadii;      // cols × rows, R32F, dot radius in css px
uniform sampler2D uClip;       // cols × 1, RG32F, (upperY, lowerY) in css px
uniform int uHasClip;
uniform float uGridFirstTileLeftX;
uniform float uPitch;
uniform float uGridStartY;
uniform float uTileSize;
uniform int uCols;
uniform int uRows;
uniform float uRasterOriginX;
uniform float uFieldWidth;
uniform float uEdgeFlushMaxDotRadius; // >0: edge-flush column slide (see below)
uniform vec4 uColor;           // straight-alpha rgba, opacity already applied
uniform int uDotShape;         // 0 = circle, 1 = square
uniform vec2 uCanvasSizeDevice;
uniform vec2 uDeviceScale;     // device px per css px

out vec4 outColor;

// Coverage of this pixel by the dot of tile (col, row); 0 when out of range.
float dotCoverage(int col, int row, vec2 cssPoint) {
  if (col < 0 || col >= uCols || row < 0 || row >= uRows) return 0.0;
  float radius = texelFetch(uRadii, ivec2(col, row), 0).r;
  if (radius <= 0.0) return 0.0;
  float tileLeft = uGridFirstTileLeftX + float(col) * uPitch;
  float tileTop = uGridStartY + float(row) * uTileSize;
  // Flush-edge alignment: dots slide from flush-left to flush-right across
  // the field so shrinking dots never get cut at either edge (mirrors
  // drawDotDither's alignT lerp). With the edge-flush column layout
  // (uEdgeFlushMaxDotRadius > 0) the slide stays within the column's max-dot
  // extent — its first/last column centers sit one max-radius in from the
  // plot edges, so a tile-relative slide would push dots past the edge
  // whenever the max radius < tileSize/2.
  float alignT = uFieldWidth > 0.0
    ? clamp((tileLeft - uRasterOriginX) / uFieldWidth, 0.0, 1.0)
    : 0.0;
  float centerX = uEdgeFlushMaxDotRadius > 0.0
    ? tileLeft + uTileSize * 0.5
      + (2.0 * alignT - 1.0) * max(uEdgeFlushMaxDotRadius - radius, 0.0)
    : tileLeft + mix(radius, uTileSize - radius, alignT);
  float centerY = tileTop + uTileSize * 0.5;
  vec2 delta = cssPoint - vec2(centerX, centerY);
  float signedDistance = uDotShape == 1
    ? max(abs(delta.x), abs(delta.y)) - radius
    : length(delta) - radius;
  // 1 DEVICE px feather ≈ the canvas rasterizer's antialiasing. The distance
  // is in css px, so scale by device px per css px — a plain css-px feather
  // is dpr× wider than the 2D path's, visibly softening dots on hidpi.
  float aaScale = max(uDeviceScale.x, uDeviceScale.y);
  return clamp(0.5 - signedDistance * aaScale, 0.0, 1.0);
}

void main() {
  // gl_FragCoord is bottom-left origin device px; convert to top-left css px.
  vec2 cssPoint = vec2(
    gl_FragCoord.x,
    uCanvasSizeDevice.y - gl_FragCoord.y
  ) / uDeviceScale;

  int col = int(floor((cssPoint.x - uGridFirstTileLeftX) / uPitch));
  int row = int(floor((cssPoint.y - uGridStartY) / uTileSize));

  // A dot can spill past its tile (radius may exceed tileSize/2, and the
  // alignment lerp shifts centers), so take the max coverage over the 3×3
  // tile neighborhood.
  float coverage = 0.0;
  for (int dRow = -1; dRow <= 1; dRow += 1) {
    for (int dCol = -1; dCol <= 1; dCol += 1) {
      coverage = max(coverage, dotCoverage(col + dCol, row + dRow, cssPoint));
    }
  }
  if (coverage <= 0.0) discard;

  if (uHasClip == 1) {
    // Per-column band clip, linearly interpolated between column centers.
    float columnPosition = clamp(
      (cssPoint.x - (uGridFirstTileLeftX + uPitch * 0.5)) / uPitch,
      0.0,
      float(uCols - 1)
    );
    int clipIndex = int(floor(min(columnPosition, float(uCols - 2))));
    float clipFraction = columnPosition - float(clipIndex);
    vec2 clipA = texelFetch(uClip, ivec2(clipIndex, 0), 0).rg;
    vec2 clipB = texelFetch(uClip, ivec2(clipIndex + 1, 0), 0).rg;
    vec2 clipBoundsHere = mix(clipA, clipB, clipFraction);
    // Same 1-device-px feather as the dot edge, for a matched crispness at
    // the band boundary.
    float clipAaScale = max(uDeviceScale.x, uDeviceScale.y);
    coverage *= clamp((cssPoint.y - clipBoundsHere.x) * clipAaScale + 0.5, 0.0, 1.0);
    coverage *= clamp((clipBoundsHere.y - cssPoint.y) * clipAaScale + 0.5, 0.0, 1.0);
    if (coverage <= 0.0) discard;
  }

  float alpha = uColor.a * coverage;
  outColor = vec4(uColor.rgb * alpha, alpha);
}
`

// Straight-alpha rgba in [0,1] from '#rgb', '#rrggbb', 'rgb()' or 'rgba()'.
// Null for anything else (caller should fall back to the canvas-2D path).
export const parseColorToRgba = (
  color: string
): [number, number, number, number] | null => {
  const hexMatch = color.trim().match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
  if (hexMatch) {
    const hex = hexMatch[1]
    const expanded = hex.length === 3
      ? hex.split('').map((c) => c + c).join('')
      : hex
    return [
      parseInt(expanded.slice(0, 2), 16) / 255,
      parseInt(expanded.slice(2, 4), 16) / 255,
      parseInt(expanded.slice(4, 6), 16) / 255,
      1,
    ]
  }
  const rgbMatch = color.trim().match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/
  )
  if (rgbMatch) {
    return [
      Number(rgbMatch[1]) / 255,
      Number(rgbMatch[2]) / 255,
      Number(rgbMatch[3]) / 255,
      rgbMatch[4] === undefined ? 1 : Number(rgbMatch[4]),
    ]
  }
  return null
}

export type DitherFieldGLRenderer = {
  render: (
    layers: DitherFieldGLLayer[],
    cssWidth: number,
    cssHeight: number,
    deviceScaleX: number,
    deviceScaleY: number
  ) => void
  dispose: () => void
  // True once the browser has force-lost this context (pages over the
  // per-page WebGL limit lose their OLDEST context — see dispose below).
  // Callers must check this per frame: rendering with a lost context is a
  // silent no-op, and blitting the dead canvas blanks the whole graph.
  isContextLost: () => boolean
}

export const createDitherFieldGLRenderer = (
  canvas: HTMLCanvasElement
): DitherFieldGLRenderer | null => {
  const gl = canvas.getContext('webgl2', {
    alpha: true,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: true,
  })
  if (!gl) return null

  const compile = (type: number, source: string): WebGLShader | null => {
    const shader = gl.createShader(type)
    if (!shader) return null
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('dither shader compile failed:', gl.getShaderInfoLog(shader))
      gl.deleteShader(shader)
      return null
    }
    return shader
  }

  const vertexShader = compile(gl.VERTEX_SHADER, VERTEX_SHADER)
  const fragmentShader = compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
  if (!vertexShader || !fragmentShader) return null
  const program = gl.createProgram()
  if (!program) return null
  gl.attachShader(program, vertexShader)
  gl.attachShader(program, fragmentShader)
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('dither shader link failed:', gl.getProgramInfoLog(program))
    return null
  }

  const uniforms = {
    radii: gl.getUniformLocation(program, 'uRadii'),
    clip: gl.getUniformLocation(program, 'uClip'),
    hasClip: gl.getUniformLocation(program, 'uHasClip'),
    gridFirstTileLeftX: gl.getUniformLocation(program, 'uGridFirstTileLeftX'),
    pitch: gl.getUniformLocation(program, 'uPitch'),
    gridStartY: gl.getUniformLocation(program, 'uGridStartY'),
    tileSize: gl.getUniformLocation(program, 'uTileSize'),
    cols: gl.getUniformLocation(program, 'uCols'),
    rows: gl.getUniformLocation(program, 'uRows'),
    rasterOriginX: gl.getUniformLocation(program, 'uRasterOriginX'),
    fieldWidth: gl.getUniformLocation(program, 'uFieldWidth'),
    edgeFlushMaxDotRadius: gl.getUniformLocation(program, 'uEdgeFlushMaxDotRadius'),
    color: gl.getUniformLocation(program, 'uColor'),
    dotShape: gl.getUniformLocation(program, 'uDotShape'),
    canvasSizeDevice: gl.getUniformLocation(program, 'uCanvasSizeDevice'),
    deviceScale: gl.getUniformLocation(program, 'uDeviceScale'),
  }

  const createDataTexture = (): WebGLTexture | null => {
    const texture = gl.createTexture()
    if (!texture) return null
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    return texture
  }

  let radiiTexture = createDataTexture()
  let clipTexture = createDataTexture()
  if (!radiiTexture || !clipTexture) return null
  // Immutable grow-only texture storage. Respecifying the textures with
  // texImage2D on every redraw (the grid dims change as the viewport
  // animates) made ANGLE/Metal allocate a fresh GPU texture per frame per
  // layer; those IOAccelerator allocations are reclaimed lazily, so a
  // scrolling reader's renderer footprint climbed into the GB range.
  // texStorage2D once per size class + texSubImage2D per frame keeps the
  // same two GPU textures alive for the life of the renderer. Dims are
  // rounded up so viewport-resize jitter doesn't re-trigger growth; the
  // shader reads via texelFetch bounded by uCols/uRows, so slack texels are
  // never sampled.
  let radiiTexW = 0
  let radiiTexH = 0
  let clipTexW = 0
  const roundUp64 = (v: number) => Math.ceil(v / 64) * 64
  const ensureRadiiStorage = (cols: number, rows: number) => {
    if (cols <= radiiTexW && rows <= radiiTexH) return
    gl.deleteTexture(radiiTexture)
    radiiTexture = createDataTexture()
    radiiTexW = roundUp64(Math.max(cols, radiiTexW))
    radiiTexH = roundUp64(Math.max(rows, radiiTexH))
    gl.bindTexture(gl.TEXTURE_2D, radiiTexture)
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.R32F, radiiTexW, radiiTexH)
  }
  const ensureClipStorage = (cols: number) => {
    if (cols <= clipTexW) return
    gl.deleteTexture(clipTexture)
    clipTexture = createDataTexture()
    clipTexW = roundUp64(Math.max(cols, clipTexW))
    gl.bindTexture(gl.TEXTURE_2D, clipTexture)
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RG32F, clipTexW, 1)
  }

  const render = (
    layers: DitherFieldGLLayer[],
    cssWidth: number,
    cssHeight: number,
    deviceScaleX: number,
    deviceScaleY: number
  ) => {
    const deviceWidth = Math.max(1, Math.ceil(cssWidth * deviceScaleX))
    const deviceHeight = Math.max(1, Math.ceil(cssHeight * deviceScaleY))
    if (canvas.width !== deviceWidth) canvas.width = deviceWidth
    if (canvas.height !== deviceHeight) canvas.height = deviceHeight
    gl.viewport(0, 0, deviceWidth, deviceHeight)
    gl.clearColor(0, 0, 0, 0)
    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.useProgram(program)
    gl.enable(gl.BLEND)
    // Premultiplied-alpha over.
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
    gl.uniform2f(uniforms.canvasSizeDevice, deviceWidth, deviceHeight)
    gl.uniform2f(uniforms.deviceScale, deviceScaleX, deviceScaleY)
    gl.uniform1i(uniforms.radii, 0)
    gl.uniform1i(uniforms.clip, 1)

    for (const layer of layers) {
      if (layer.opacity <= 0 || layer.cols <= 0 || layer.rows <= 0) continue
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
      gl.activeTexture(gl.TEXTURE0)
      ensureRadiiStorage(layer.cols, layer.rows)
      gl.bindTexture(gl.TEXTURE_2D, radiiTexture)
      gl.texSubImage2D(
        gl.TEXTURE_2D, 0, 0, 0, layer.cols, layer.rows,
        gl.RED, gl.FLOAT, layer.radii
      )
      gl.activeTexture(gl.TEXTURE1)
      if (layer.clipBounds) ensureClipStorage(layer.cols)
      gl.bindTexture(gl.TEXTURE_2D, clipTexture)
      if (layer.clipBounds) {
        gl.texSubImage2D(
          gl.TEXTURE_2D, 0, 0, 0, layer.cols, 1,
          gl.RG, gl.FLOAT, layer.clipBounds
        )
      }
      gl.uniform1i(uniforms.hasClip, layer.clipBounds ? 1 : 0)
      gl.uniform1f(uniforms.gridFirstTileLeftX, layer.gridFirstTileLeftX)
      gl.uniform1f(uniforms.pitch, layer.pitch)
      gl.uniform1f(uniforms.gridStartY, layer.gridStartY)
      gl.uniform1f(uniforms.tileSize, layer.tileSize)
      gl.uniform1i(uniforms.cols, layer.cols)
      gl.uniform1i(uniforms.rows, layer.rows)
      gl.uniform1f(uniforms.rasterOriginX, layer.rasterOriginX)
      gl.uniform1f(uniforms.fieldWidth, layer.fieldWidth)
      gl.uniform1f(
        uniforms.edgeFlushMaxDotRadius,
        layer.edgeFlushMaxDotRadius ?? 0
      )
      gl.uniform4f(
        uniforms.color,
        layer.color[0],
        layer.color[1],
        layer.color[2],
        layer.color[3] * layer.opacity
      )
      gl.uniform1i(uniforms.dotShape, layer.dotShape === 'square' ? 1 : 0)
      gl.drawArrays(gl.TRIANGLES, 0, 3)
    }
  }

  const dispose = () => {
    gl.deleteTexture(radiiTexture)
    gl.deleteTexture(clipTexture)
    gl.deleteProgram(program)
    gl.deleteShader(vertexShader)
    gl.deleteShader(fragmentShader)
    // Deleting the GL objects above does NOT free the WebGL2 context itself —
    // it lingers until the canvas is GC'd, and Safari/iOS reclaim contexts
    // lazily. Any component that remounts this renderer (the mobile dashboard
    // graph is re-keyed on every dashboard open) therefore piles up live
    // contexts until the browser hits its small per-page WebGL limit and
    // force-loses the OLDEST one — in practice a globe canvas, which blanks to a
    // gray box after ~4-5 opens. Explicitly release it so remounts don't
    // accumulate.
    gl.getExtension('WEBGL_lose_context')?.loseContext()
  }

  return { render, dispose, isContextLost: () => gl.isContextLost() }
}
